// useDailyBonus — local-only daily login XP bonus tracker for Beginner
// Mode. No DB table required; we persist the last claim date (UTC) in
// localStorage so the bonus refreshes once per calendar day.
//
// Returns:
//   available  — whether the bonus can be claimed right now
//   claim()    — atomically marks today's bonus as claimed
//   amount     — XP awarded (constant for now; could grow with streak)
import { useCallback, useEffect, useState } from "react";

const KEY = "hl.dailyBonus.lastClaim.v1";
const AMOUNT = 25;

function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

function readLastClaim(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function useDailyBonus() {
  const [lastClaim, setLastClaim] = useState<string | null>(() =>
    typeof window === "undefined" ? null : readLastClaim(),
  );

  // Cross-tab sync.
  useEffect(() => {
    const onStorage = () => setLastClaim(readLastClaim());
    window.addEventListener("storage", onStorage);
    window.addEventListener("hl:dailyBonus:change", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("hl:dailyBonus:change", onStorage);
    };
  }, []);

  const today = todayUtc();
  const available = lastClaim !== today;

  const claim = useCallback((): boolean => {
    if (!available) return false;
    try {
      localStorage.setItem(KEY, today);
      setLastClaim(today);
      window.dispatchEvent(new CustomEvent("hl:dailyBonus:change"));
      return true;
    } catch {
      return false;
    }
  }, [available, today]);

  return { available, amount: AMOUNT, claim };
}
